<script setup>
import { computed } from "vue";
import { buildStackedBarChartOptions } from "../analysisCharts";
import { formatMoneyFlexible } from "../analysisFormat";

const props = defineProps({
  title: { type: String, required: true },
  categories: { type: Array, default: () => [] },
  series: { type: Array, default: () => [] },
  isMoney: { type: Boolean, default: false },
  // Stacked (the default) for "how does this dimension break down within
  // each date bucket" — grouped (side-by-side) is only used where stacking
  // would combine values that shouldn't be summed together, e.g. revenue
  // across different currencies.
  stacked: { type: Boolean, default: true },
  isLoading: { type: Boolean, default: false },
  errorMessage: { type: String, default: "" },
});

const chartOptions = computed(() =>
  buildStackedBarChartOptions(props.categories, props.series, {
    isMoney: props.isMoney,
    stacked: props.stacked,
  })
);

const hasData = computed(
  () => props.categories.length > 0 && props.series.some((s) => (s.data || []).some((v) => v))
);

const total = computed(() =>
  props.series.reduce((sum, s) => sum + (s.data || []).reduce((a, v) => a + (Number(v) || 0), 0), 0)
);
const formattedTotal = computed(() =>
  props.isMoney ? formatMoneyFlexible(total.value) : Math.round(total.value).toLocaleString()
);
</script>

<template>
  <div class="card h-100">
    <div class="card-body chart-card-body">
      <div class="d-flex justify-content-between align-items-start mb-2">
        <h6 class="mb-0">{{ title }}</h6>
        <span v-if="!isLoading && !errorMessage && hasData" class="text-sm text-secondary">
          {{ formattedTotal }}
        </span>
      </div>

      <div v-if="isLoading" class="d-flex align-items-center justify-content-center" style="height: 260px">
        <span class="spinner-border spinner-border-sm text-success"></span>
      </div>
      <div v-else-if="errorMessage" class="text-xs text-danger">{{ errorMessage }}</div>
      <div v-else-if="!hasData" class="text-xs text-secondary d-flex align-items-center justify-content-center"
        style="height: 260px">
        No data for the selected filters.
      </div>
      <apexchart v-else type="bar" height="320" :options="chartOptions" :series="series" />
    </div>
  </div>
</template>

<style scoped>
/* Bootstrap/Argon's default .card-body padding (1rem all around) leaves too
   much dead space around a fixed-height chart in a 3-up grid — tighten it
   here rather than globally, so other cards on the page keep their padding. */
.card .chart-card-body {
  padding: 0.75rem 0.85rem;
}
</style>
